import { API_URL as baseURL } from "../../config";
import { saveAs } from "file-saver";

const api = ({ companyID, cookieSession }) => {
  const isReady = Boolean(companyID && cookieSession);

  const jsonResolver = (res) => {
    return res.json();
  };

  const blobResolver = (res) => {
    return res.blob();
  };

  const getApiPromise = async ({
    path,
    params,
    method,
    headers,
    body,
    resolver = jsonResolver,
  }) => {
    const url = new URL(`${baseURL}${path}`);
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        url.searchParams.set(key, value);
      }
    }
    const init = {
      ...(method && { method }),
      credentials: "include",
      headers: {
        ...headers,
      },
      ...(body && { body }),
    };

    if (!isReady) {
      throw new Error("No data to connect to ControlHub API");
    }

    return new Promise((resolve, reject) => {
      fetch(url, init)
        .then((res) => resolver(res))
        .then((data) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  return {
    isReady,
    getCurrentUser: async () =>
      getApiPromise({
        path: `/app_api/users/me`,
      }),
    getLogout: async () =>
      getApiPromise({
        path: `/admin/session/logout`,
      }),
    getUsers: async () => getApiPromise({ path: `/admin/${companyID}/users` }),
    getPurchaseOrders: async ({ search }) =>
      getApiPromise({
        path: `/admin/${companyID}/purchase_orders`,
        params: {
          page: 1,
          pageSize: 1000,
          search,
        },
      }),
    getRequests: async ({ search }) =>
      getApiPromise({
        path: `/admin/${companyID}/requests`,
        params: {
          page: 1,
          pageSize: 1000,
          search,
        },
      }),
    createInvoice: async ({ invoiceData }) =>
      getApiPromise({
        path: `/admin/${companyID}/invoices`,
        method: "POST",
        body: invoiceData,
      }),
    createBill: async ({ invoiceID, data }) =>
      getApiPromise({
        path: `/admin/${companyID}/invoices/${invoiceID}`,
        method: "POST",
        body: JSON.stringify(data),
      }),
    deleteBill: async ({ billID }) =>
      getApiPromise({
        path: `/admin/${companyID}/invoices/${billID}`,
        method: "DELETE",
      }),
    getBill: async ({ billID }) =>
      getApiPromise({ path: `/admin/${companyID}/invoices/${billID}` }),
    getBills: async ({ filters, search }) =>
      getApiPromise({
        path: `/admin/${companyID}/invoices`,
        params: {
          filters: JSON.stringify(filters),
          page: 1,
          pageSize: 1000,
          search,
        },
      }),
    approveBill: async ({ billID }) =>
      getApiPromise({
        path: `/admin/${companyID}/invoices/${billID}/approve`,
        method: "POST",
      }),
    cancelBillPayment: async ({ billID }) =>
      getApiPromise({
        path: `/admin/${companyID}/invoices/${billID}/cancel_payment`,
        method: "POST",
      }),
    setBillPayment: async ({ billID, date }) =>
      getApiPromise({
        path: `/admin/${companyID}/invoices/${billID}/pay`,
        method: "POST",
        body: JSON.stringify({ date }),
      }),
    pingBillApprover: async ({ billID }) =>
      getApiPromise({
        path: `/admin/${companyID}/invoices/${billID}/ping`,
        method: "POST",
      }),
    sendBillToAPSystem: async ({ billID }) =>
      getApiPromise({
        path: `/admin/${companyID}/invoices/${billID}/send_email_bill_dot_com`,
      }),
    saveBill: async ({ billID, billData }) =>
      getApiPromise({
        path: `/admin/${companyID}/invoices/${billID}`,
        method: "PATCH",
        body: JSON.stringify(billData),
      }),
    submitBill: async ({ billID }) =>
      getApiPromise({
        path: `/admin/${companyID}/invoices/${billID}/submit`,
        method: "POST",
      }),
    createVendor: async ({ vendorData }) =>
      getApiPromise({
        path: `/admin/${companyID}/vendors`,
        method: "POST",
        body: JSON.stringify(vendorData),
      }),
    replaceVendor: async ({ vendorID, vendorData }) =>
      getApiPromise({
        path: `/admin/${companyID}/vendors`,
        method: "PUT",
        body: JSON.stringify({ id: vendorID, ...vendorData }),
      }),
    getVendor: async ({ vendorID }) =>
      getApiPromise({
        path: `/admin/${companyID}/vendors/${vendorID}`,
      }),
    getVendors: async ({ preferred }) =>
      getApiPromise({
        path: `/admin/${companyID}/vendors`,
        params: {
          preferred,
        },
      }),
    getVendorsTerms: async () =>
      getApiPromise({
        path: `/admin/${companyID}/terms`,
      }),
    getPaymentMethods: async () =>
      getApiPromise({
        path: `/admin/companies/${companyID}/payment_types`,
      }),
    getAccountingConfiguration: async () =>
      getApiPromise({
        path: `/admin/companies/${companyID}/accounting`,
      }),
    connectAccountingSystem: async ({ systemName }) =>
      getApiPromise({
        path: `/admin/companies/${companyID}/accounting/${systemName}/connect`,
      }),
    disconnectAccountingSystem: async ({ systemName }) =>
      getApiPromise({
        path: `/admin/companies/${companyID}/accounting/${systemName}/disconnect`,
        method: "POST",
      }),
    syncAccountingSystem: async ({ systemName }) =>
      getApiPromise({
        path: `/admin/companies/${companyID}/accounting/${systemName}/sync`,
        method: "POST",
      }),
    activateAccountingAccounts: async ({ accountIDs }) =>
      getApiPromise({
        path: `/admin/companies/${companyID}/accounting/activate`,
        method: "PUT",
        body: JSON.stringify(accountIDs),
      }),
    deactivateAccountingAccounts: async ({ accountIDs }) =>
      getApiPromise({
        path: `/admin/companies/${companyID}/accounting/deactivate`,
        method: "PUT",
        body: JSON.stringify(accountIDs),
      }),
    getAccountingClassAccounts: async () =>
      getApiPromise({
        path: `/admin/companies/${companyID}/accounting/classes`,
      }),
    getAccountingExpenseAccounts: async () =>
      getApiPromise({
        path: `/admin/companies/${companyID}/accounting/accounts`,
      }),
    setAccountingAccountCustomName: async ({ accountID, customName }) =>
      getApiPromise({
        path: `/admin/companies/${companyID}/accounting/element/${accountID}`,
        method: "PUT",
        body: JSON.stringify({ custom_name: customName }),
      }),
    getAccountingCategories: async () =>
      getApiPromise({
        path: `/admin/${companyID}/accounting/categories`,
      }),
    getFile: async (fileId) =>
      getApiPromise({
        path: `/admin/files/${fileId}`,
      }),
    getFullInvoice: async (invoiceID, filename) =>
      getApiPromise({
        path: `/admin/${companyID}/invoices/${invoiceID}/pdf`,
        resolver: blobResolver,
      }).then((blob) => saveAs(blob, filename)),
    customerPortal: async () =>
      getApiPromise({
        path: `/admin/companies/${companyID}/create-customer-portal-session`,
        method: "POST",
      }),
    getBillingData: async () =>
      getApiPromise({
        path: "/billing",
        params: {
          companyId: companyID,
        },
      }),
  };
};

export default api;
